import React from 'react';
import { Link } from 'react-router-dom';
import { Column } from '../../components/DataTable/DataTable';
import Badge from '../../components/ui/Badge';
import { FieldConfig } from '../../components/generic/fieldRenderers';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../config/appConfig';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import type { WrhDay, WrhDayStatus } from './useData';

export const formatWrhStatus = (status: WrhDayStatus) => (status === 'OPEN' ? 'Open' : 'Closed');

export const moduleConfig = {
  module: {
    title: 'Working Rest Hours',
    permissionModuleKey: 'wrh',
    route: {
      list: '/wrh',
      new: '/wrh/sheet',
      detail: (_id: number) => `/wrh/sheet`,
      edit: (_id: number) => `/wrh/sheet`,
      sheetForDay: (item: { vesselId: number; workDate: string }) => {
        const month = String(item.workDate).slice(0, 7);
        return `/wrh/sheet?vesselId=${item.vesselId}&month=${month}`;
      },
      compliance: '/wrh/compliance',
      reports: '/wrh/reports'},
    api: {
      list: '/wrh/days',
      detail: (id: number) => `/wrh/days/${id}`}},
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions},
  search: { placeholder: 'Search by vessel…' },
  filterGroups: [
    { name: 'basic', label: 'Basic', icon: '', fields: ['status'] },
  ],
  filters: {
    fields: [
      {
        key: 'status',
        label: 'Status',
        type: 'select' as const,
        getValue: (item: WrhDay) => item.status,
        options: [
          { value: 'OPEN', label: 'Open' },
          { value: 'CLOSED', label: 'Closed' },
        ]},
    ],
    applyFilter: (items: WrhDay[], filters: Record<string, string[]>) =>
      items.filter((item) => {
        if (filters.status?.length && !filters.status.includes(item.status)) return false;
        return true;
      })},
  toolbar: {
    actions: []},
  table: {
    actionsPosition: 'left' as const,
    keyExtractor: (item: WrhDay) => item.id,
  },
  emptyStates: {
    noData: 'No WRH days yet. Use the Month sheet to open and fill days.',
    noSearchResults: 'No WRH days match your search.',
    noFilterResults: 'No WRH days match the filters.',
  },
  delete: {
    title: 'Delete WRH day',
    message: 'Delete this day and all crew entries?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Could not delete WRH day.',
    handler: async (id: number) => {
      const { deleteWrhDay } = await import('../../services/mutations');
      await deleteWrhDay(id);
    }},
  utils: {
    formatDate: (dateString?: string) => (dateString ? formatDateGlobal(dateString) : '')},
  detail: {
    subtitle: 'WRH day',
    notFoundTitle: 'Not found',
    notFoundMessage: 'This WRH day does not exist.',
    notFoundBackText: 'Back to list',
    defaultTitle: 'WRH day',
    getTitle: (item: WrhDay) =>
      `${item.vessel?.vesselName ?? `Vessel #${item.vesselId}`} — ${formatDateGlobal(item.workDate)}`},
  fields: [] as FieldConfig[]};

const TextCell: React.FC<{ value: string | null | undefined }> = ({ value }) => {
  const { colors } = useTheme();
  return <span className={cn('text-sm whitespace-nowrap', colors.text)}>{value || ''}</span>;
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<WrhDay>[] => [
  {
    key: 'vessel.vesselName',
    header: 'Vessel',
    sortable: true,
    render: (_v, row) => <TextCell value={row.vessel?.vesselName} />},
  {
    key: 'workDate',
    header: 'Date',
    sortable: true,
    render: (v) => formatDate(v)},
  {
    key: 'status',
    header: 'Status',
    sortable: true,
    render: (v, row) => (
      <Badge
        variant={(v ?? row.status) === 'OPEN' ? 'success' : 'secondary'}
        className="shrink-0 border rounded-md px-2 py-0.5"
      >
        {formatWrhStatus((v ?? row.status) as WrhDayStatus)}
      </Badge>
    )},
  {
    key: '_count.crewDays',
    header: 'Crew',
    sortable: false,
    render: (_v, row) => <TextCell value={String(row._count?.crewDays ?? row.crewDays?.length ?? 0)} />},
  {
    key: 'ncCount',
    header: 'NC',
    sortable: false,
    render: (_v, row) => {
      const n = row.ncCount ?? 0;
      return n > 0 ? (
        <Badge variant="danger" className="rounded-md px-2 py-0.5">
          {n}
        </Badge>
      ) : (
        <TextCell value="0" />
      );
    }},
];

const TableActionsCell: React.FC<{
  item: WrhDay;
  onDelete: (id: number) => void;
  canDelete?: boolean;
}> = ({ item, onDelete, canDelete = true }) => (
  <div className="flex items-center flex-nowrap">
    <Link
      to={moduleConfig.module.route.sheetForDay(item)}
      className="p-1 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-lg transition-colors"
      onClick={(e) => e.stopPropagation()}
      title="Open in month sheet"
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
        />
      </svg>
    </Link>
    {canDelete && item.status !== 'CLOSED' && (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(item.id);
        }}
        className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
        title="Delete"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
          />
        </svg>
      </button>
    )}
  </div>
);

export const getTableActions = (
  item: WrhDay,
  onDelete: (id: number) => void,
  _c?: unknown,
  _ctx?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean },
): React.ReactNode => (
  <TableActionsCell item={item} onDelete={onDelete} canDelete={options?.canDelete !== false} />
);

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  if (iconName === 'add') {
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    );
  }
  return null;
};
